import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, Flag, ClipboardList, Church, Award, Bot, FileBarChart, LayoutDashboard, AlertTriangle, Lightbulb, CalendarClock } from "lucide-react";
import { api, has } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { StatCard } from "../components/app/StatCard";
import { DistChart } from "../components/app/DistChart";
import { PageHeader } from "../components/app/PageHeader";

const TILES = [
  { id: "overview", title: "Parish Overview", icon: LayoutDashboard, to: "/dashboard" },
  { id: "realities", title: "Our People's Realities", icon: Users2, to: "/realities" },
  { id: "priorities", title: "Priority Pastoral Concerns", icon: Flag, to: "/priorities" },
  { id: "plans", title: "Pastoral Action Plans", icon: ClipboardList, to: "/plans" },
  { id: "gkk", title: "GKK Community Profiles", icon: Church, to: "/gkk-profiles" },
  { id: "mission", title: "Mission Accomplishments", icon: Award, to: "/evaluation" },
  { id: "ai", title: "AI Pastoral Analyst", icon: Bot, to: "/analyst", perm: "analyst:use" },
  { id: "reports", title: "Reports and Evaluation", icon: FileBarChart, to: "/reports" },
];

const Tile = ({ tile, t, i }) => {
  const body = (
    <div className={`rise group h-full rounded-xl border p-5 transition-[transform,border-color] ${tile.phase ? "border-dashed border-stone-300 bg-white/60" : "border-amber-700/20 bg-white hover:-translate-y-0.5 hover:border-amber-700/50"}`}
      style={{ animationDelay: `${i * 50}ms` }} data-testid={`tile-${tile.id}`}>
      <tile.icon className={`h-6 w-6 ${tile.phase ? "text-stone-400" : "text-amber-900"}`} />
      <p className={`mt-4 font-serif-display text-xl font-semibold ${tile.phase ? "text-stone-500" : "text-amber-950"}`}>{tile.title}</p>
      <p className="mt-1 text-xs text-stone-500">{tile.phase ? `${t("coming")} Phase ${tile.phase}` : tile.locked ? "Parish Priest & administrators only" : `${t("open")} →`}</p>
    </div>
  );
  return tile.to ? <Link to={tile.to}>{body}</Link> : body;
};

const DataSource = ({ src }) => (
  <div className="rounded-xl border border-amber-700/15 bg-[#F5F0E6] p-5 text-sm" data-testid="data-source-card">
    <p className="flex items-center gap-2 font-semibold text-amber-950"><CalendarClock className="h-4 w-4" /> Data source</p>
    <ul className="mt-3 space-y-1 text-stone-700">
      <li>Survey periods: <b>{src.periods.join(", ") || "none"}</b></li>
      <li>Survey dates: {src.earliest_survey_date || "—"} to {src.latest_survey_date || "—"}</li>
      <li>Last import: {src.last_import_at ? new Date(src.last_import_at).toLocaleString() : "never"}</li>
    </ul>
    {src.outdated && <p className="mt-3 text-amber-800" data-testid="data-outdated-warning">The latest survey is more than a year old.</p>}
    {src.incomplete && <p className="mt-2 text-amber-800" data-testid="data-incomplete-warning">Some GKKs have no survey records yet — results are incomplete.</p>}
    {src.includes_test_data && <p className="mt-2 font-semibold text-amber-900">Includes TEST sample data.</p>}
  </div>
);

export const Insights = ({ items, caveat }) => (
  <div className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid="insights-panel">
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3" data-testid={`insight-${i}`}>
          {it.type === "finding" ? <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-[#285E3B]" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />}
          <div><p className="text-sm text-stone-800">{it.text}</p><p className="text-xs text-stone-500">{it.evidence}</p></div>
        </li>
      ))}
      {!items.length && <li className="text-sm text-stone-500">Not enough data yet to describe patterns.</li>}
    </ul>
    <p className="mt-5 border-t border-amber-700/10 pt-3 text-xs italic text-stone-500">{caveat}</p>
  </div>
);

const PublicView = ({ t }) => {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/public/overview").then((r) => setO(r.data)).catch(() => {}); }, []);
  if (!o) return null;
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard label={t("households")} value={o.households_latest_period} note={o.latest_period ? `Survey period ${o.latest_period}` : "No survey yet"} testId="public-households-metric" />
      <StatCard label={t("gkks_part")} value={o.gkks_participating} note={`of ${o.gkks_total} GKKs in the master list`} testId="public-gkks-metric" />
      <Link to="/programs" className="rounded-xl bg-amber-900 p-6 text-amber-50 hover:bg-[#5B210B] transition-colors" data-testid="public-programs-link">
        <p className="font-serif-display text-2xl font-semibold">Rate a parish program</p>
        <p className="mt-2 text-sm text-amber-100">Tell us, voluntarily and anonymously, whether a program helped you.</p>
      </Link>
      <Link to="/feedback" className="rounded-xl bg-amber-900 p-6 text-amber-50 hover:bg-[#5B210B] transition-colors" data-testid="public-feedback-link">
        <p className="font-serif-display text-2xl font-semibold">Share a concern or suggestion</p>
        <p className="mt-2 text-sm text-amber-100">Your voice helps the parish discern how to serve. Submissions are voluntary.</p>
      </Link>
    </div>
  );
};

const Programs = ({ s }) => (
  <section className="grid gap-6 lg:grid-cols-[1fr_1.2fr]" data-testid="programs-section">
    <div className="grid grid-cols-2 gap-4">
      <StatCard label="Approved & ongoing programs" value={(s.status_counts.approved || 0) + (s.status_counts.in_progress || 0)} testId="programs-active-metric" note={`${s.status_counts.completed || 0} completed · ${s.status_counts.evaluated || 0} evaluated`} />
      <StatCard label="Households reached" value={s.monitoring.households_reached} testId="households-reached-metric" note={`${s.monitoring.participants_served} participants recorded`} tip="Sum of households reached reported in monitoring entries. A household reached by two programs is counted twice." />
      <StatCard label="Plans awaiting decision" value={s.status_counts.under_review || 0} testId="plans-review-metric" note={`${s.status_counts.proposed || 0} newly proposed`} />
      <StatCard label="Priorities proposed" value={s.priorities.proposed} testId="priorities-proposed-metric" note={`${s.priorities.approved} approved`} />
    </div>
    <div className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid="follow-up-panel">
      <p className="font-semibold text-stone-800">Programs requiring follow-up or evaluation</p>
      <ul className="mt-3 space-y-2">
        {s.follow_up.map((p) => (
          <li key={p.plan_id}><Link to={`/plans/${p.plan_id}`} className="flex justify-between gap-3 text-sm hover:text-amber-900"><span className="text-stone-800">{p.concern}</span><span className="text-xs text-amber-800">{p.reason}</span></Link></li>
        ))}
        {!s.follow_up.length && <li className="text-sm text-stone-500">Nothing pending.</li>}
      </ul>
    </div>
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [d, setD] = useState(null);
  const canAnalyze = has(user, "analytics:all", "analytics:own_gkk");
  const [err, setErr] = useState("");
  const [prog, setProg] = useState(null);

  useEffect(() => {
    if (canAnalyze) api.get("/analytics").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
    if (has(user, "plan:propose")) api.get("/plans/summary").then((r) => setProg(r.data)).catch(() => {});
  }, [canAnalyze, user]);

  return (
    <div className="space-y-10">
      <PageHeader eyebrow="Saint Francis of Assisi Parish" title="MABUHAY PASTORAL INTELLIGENCE" testId="dashboard-header">
        <span className="font-serif-display italic text-lg text-amber-900">{t("subtitle")}</span>
      </PageHeader>
      {!canAnalyze && <PublicView t={t} />}
      {err && <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900" data-testid="dashboard-error">{err}</p>}
      {d && (
        <>
          {d.scope === "own_gkk" && <p className="text-sm text-[#285E3B]" data-testid="scope-note">Showing your own GKK only.</p>}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label={t("households")} value={d.totals.households} testId="households-surveyed-metric" tip="Each household is counted once per survey period. These are survey records, not a census of the parish." />
            <StatCard label={t("respondents")} value={d.totals.respondents} delay={60} testId="respondents-metric" note={`${d.totals.respondents_missing} households did not report this`} tip="People who personally answered the survey. Missing answers are not counted as zero." />
            <StatCard label={t("individuals")} value={d.totals.individuals_in_households} delay={120} testId="individuals-metric" note={`${d.totals.household_size_missing} households without size`} tip="Total people living in surveyed households, as reported. This is not a population estimate." />
            <StatCard label={t("gkks_part")} value={d.participation.filter((p) => p.households > 0).length} delay={180} testId="gkks-participating-metric" note={`of ${d.participation.length} in the master list`} tip="GKK (Gagmay'ng Kristohanong Katilingban) — Basic Ecclesial Community." />
          </div>
          <section>
            <h2 className="mb-4 font-serif-display text-2xl font-semibold text-amber-900">{t("tiles")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{TILES.map((tile, i) => <Tile key={tile.id} tile={tile.perm && !has(user, tile.perm) ? { ...tile, to: null, locked: true } : tile} t={t} i={i} />)}</div>
          </section>
          {prog && <Programs s={prog} />}
          <section className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="mb-4 font-serif-display text-2xl font-semibold text-amber-900">{t("insights")}</h2>
              <Insights items={d.insights} caveat={d.caveat} />
            </div>
            <div>
              <h2 className="mb-4 font-serif-display text-2xl font-semibold text-amber-900">{t("data_source")}</h2>
              <DataSource src={d.data_source} />
            </div>
          </section>
          {d.distributions.concerns && (
            <section className="grid gap-6 lg:grid-cols-2">
              <DistChart title="Major concerns identified by households" dist={d.distributions.concerns} testId="dash-concerns-chart" />
              <DistChart title="Sunday Mass participation" dist={d.distributions.mass_attendance} testId="dash-mass-chart" />
            </section>
          )}
        </>
      )}
    </div>
  );
}
